import React, { useEffect, useMemo, useState } from "react";
import { activeBundleOffers, computeBundleLineTotal, normalizeBundleOffers } from "../../utils/bundleOffers";
import BundleOfferPickerModal from "./BundleOfferPickerModal";
import "../CatalogStudioForms.css";

function offerDisplayPrice(offer, productsById) {
  const previewPicks = Array.from({ length: offer.pick_count }, () => ({}));
  const fromPrice = computeBundleLineTotal(offer, previewPicks, productsById);
  return offer.pricing_mode === "fixed" ? Number(offer.offer_price) || fromPrice : fromPrice;
}

function BundleOfferCard({
  offer,
  displayPrice,
  primaryColor,
  secondaryColor,
  canOrder,
  onPick,
}) {
  const img = String(offer.image_url || "").trim();

  if (!img) {
    return (
      <article className="catalog-bundle-offer-row">
        <div className="catalog-bundle-offer-row__info">
          <h3>{offer.name}</h3>
          {offer.description ? <p>{offer.description}</p> : null}
        </div>
        <div className="catalog-bundle-offer-row__end">
          <div className="catalog-bundle-offer-row__price" style={{ color: primaryColor }}>
            ₪{displayPrice}
          </div>
          {canOrder ? (
            <button
              type="button"
              className="catalog-btn catalog-bundle-offer-row__btn"
              style={{ background: primaryColor, color: secondaryColor }}
              onClick={() => onPick(offer)}
            >
              اختر العرض
            </button>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article className="catalog-bundle-offer-card catalog-bundle-offer-card--featured">
      <div className="catalog-bundle-offer-card__media catalog-bundle-offer-card__media--featured">
        <img src={img} alt={offer.name} width={640} height={360} loading="lazy" decoding="async" />
      </div>
      <div className="catalog-bundle-offer-card__body">
        <h3>{offer.name}</h3>
        {offer.description ? <p>{offer.description}</p> : null}
        <div className="catalog-bundle-offer-card__foot">
          <div className="catalog-bundle-offer-card__price" style={{ color: primaryColor }}>
            ₪{displayPrice}
          </div>
          {canOrder ? (
            <button
              type="button"
              className="catalog-btn catalog-bundle-offer-card__btn catalog-bundle-offer-card__btn--featured"
              style={{ background: primaryColor, color: secondaryColor }}
              onClick={() => onPick(offer)}
            >
              اختر العرض
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function BundleOffersCarousel({
  offers,
  productsById,
  primaryColor,
  secondaryColor,
  canOrder,
  onPick,
}) {
  const [idx, setIdx] = useState(0);
  const n = offers.length;

  useEffect(() => {
    setIdx(0);
  }, [n]);

  useEffect(() => {
    const id = setInterval(() => setIdx((p) => (p + 1) % n), 8000);
    return () => clearInterval(id);
  }, [n]);

  const goPrev = () => setIdx((p) => (p - 1 + n) % n);
  const goNext = () => setIdx((p) => (p + 1) % n);

  return (
    <div className="catalog-bundle-offers-carousel">
      <div className="catalog-bundle-offers-carousel__chrome">
        <div className="catalog-bundle-offers-carousel__viewport">
          <div
            className="catalog-bundle-offers-carousel__track"
            style={{
              transform: `translateX(-${idx * 100}%)`,
            }}
          >
            {offers.map((offer) => (
              <div key={offer.id} className="catalog-bundle-offers-carousel__slide">
                <BundleOfferCard
                  offer={offer}
                  displayPrice={offerDisplayPrice(offer, productsById)}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  canOrder={canOrder}
                  onPick={onPick}
                />
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="catalog-bundle-offers-carousel__nav catalog-bundle-offers-carousel__nav--prev"
          onClick={goPrev}
          aria-label="العرض السابق"
        >
          ›
        </button>
        <button
          type="button"
          className="catalog-bundle-offers-carousel__nav catalog-bundle-offers-carousel__nav--next"
          onClick={goNext}
          aria-label="العرض التالي"
        >
          ‹
        </button>
        <div className="catalog-bundle-offers-carousel__dots" aria-hidden="true">
          {offers.map((offer, dotIdx) => (
            <button
              key={offer.id}
              type="button"
              className={`catalog-bundle-offers-carousel__dot${dotIdx === idx ? " is-active" : ""}`}
              onClick={() => setIdx(dotIdx)}
              aria-label={`عرض ${dotIdx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BundleOffersSection({
  sectionId,
  settings = {},
  externalOffers = null,
  catalogId = "",
  frame,
  fg,
  titlePx,
  subtitlePx,
  primaryColor,
  secondaryColor,
  products = [],
  productsById,
  showStorefrontCart,
  menuOnly,
  onAddToCart,
}) {
  const st = settings || {};
  const offers = useMemo(() => {
    if (externalOffers != null) {
      return normalizeBundleOffers(externalOffers).filter((o) => o.is_active && o.name);
    }
    return activeBundleOffers(st);
  }, [externalOffers, st]);
  const [pickerOffer, setPickerOffer] = useState(null);

  if (st.visible === false || offers.length === 0) return null;

  const canOrder = showStorefrontCart && !menuOnly;
  const useCarousel = offers.length > 1;

  return (
    <>
      <section
        className="catalog-bundle-offers-section catalog-bundle-offers-section--featured"
        style={{
          background: st?.style?.backgroundColor || "transparent",
          color: fg || st?.style?.textColor || "#0f172a",
          padding: frame?.pad || "12px 0",
          direction: "rtl",
        }}
      >
        {st.title ? (
          <div className="catalog-bundle-offers-section__header">
            <div style={{ fontWeight: 950, fontSize: titlePx }}>{st.title}</div>
            {st.subtitle ? (
              <div style={{ marginTop: 4, fontSize: subtitlePx, opacity: 0.85, lineHeight: 1.5 }}>
                {st.subtitle}
              </div>
            ) : null}
          </div>
        ) : null}

        {useCarousel ? (
          <BundleOffersCarousel
            offers={offers}
            productsById={productsById}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            canOrder={canOrder}
            onPick={setPickerOffer}
          />
        ) : (
          <BundleOfferCard
            offer={offers[0]}
            displayPrice={offerDisplayPrice(offers[0], productsById)}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            canOrder={canOrder}
            onPick={setPickerOffer}
          />
        )}
      </section>

      <BundleOfferPickerModal
        open={Boolean(pickerOffer)}
        offer={pickerOffer}
        catalogId={catalogId}
        products={products}
        productsById={productsById}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        onClose={() => setPickerOffer(null)}
        onConfirm={(data) => onAddToCart({ ...data, sectionId })}
      />
    </>
  );
}
